import { ChannelName } from "../ui/channel.tsx";
import { ArchiveIcon, ArrowLeftIcon, ArticleIcon, CubeIcon, GitBranchIcon, ListChecksIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, q } from "../api.ts";
import { useAttention } from "../attention.ts";
import { notify } from "../feedback.ts";
import { dateTime, minutes, number, usd } from "../format.ts";
import { useInboxSeen } from "../inbox-seen.ts";
import { AREA_LABELS as A, INBOX_LABELS as I, PLAN_REVIEW_LABELS as R } from "../labels.ts";
import { readPref, writePref } from "../prefs.ts";
import { planChecks, planOutline, planTotals } from "../plan-review.ts";
import { Button, Callout, ErrorText, Kbd, Modal, Page } from "../ui.tsx";
import { AgentLogo, agentEngine, PropertyList, PropertyRow } from "../ui/props.tsx";
import { AgentText, CheckList, Excerpt, PointList, Glance, More, ReviewBar, ReviewCard, ReviewOutline } from "../ui/review.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { Chip, StatusChip } from "../ui/status.tsx";
import { planStatus } from "../week-items.ts";

// Plan review as a full page (docs/design-system.md §0.4d, plan M7 step 7f),
// shaped like a pull-request review: an outline on the left, the plan in one
// look, what the server checked, one card per campaign and per action, and a
// bar with the decision. Summary first (§0.3e): what will happen, where, the
// tier, the pre-authorization, the measurement and the cost are always open;
// the agent's long text shows its first lines; what a decision does not turn
// on folds. "Viewed" is the person's own mark in this browser; it never folds
// a card and never gates approval.

const typing = (target: EventTarget | null) => target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
const viewedKey = (planId: string) => `plan-viewed:${planId}`;

function readViewed(planId: string): Set<string> {
  try { return new Set(JSON.parse(readPref(viewedKey(planId)) ?? "[]") as string[]); }
  catch { return new Set(); }
}

export function PlanReviewPage() {
  const { productId, planId } = useParams({ strict: false }) as { productId: string; planId: string };
  const navigate = useNavigate();
  const client = useQueryClient();
  const plan = useQuery(q.agentPlan(planId));
  const metrics = useQuery(q.metrics(productId));
  const goals = useQuery(q.goals(productId));
  const products = useQuery(q.products);
  const inbox = useQuery(q.inbox);
  const { markSeen } = useInboxSeen();
  const item = inbox.data?.items.find((entry) => entry.kind === "plan" && entry.id === planId);
  useEffect(() => { if (item) markSeen(item); }, [item, markSeen]);
  const pending = Boolean(plan.data && plan.data.status === "proposed" && (!plan.data.expires_at || Date.parse(plan.data.expires_at) > Date.now()));
  useAttention(pending ? productId : null);

  const [viewed, setViewed] = useState(() => readViewed(planId));
  const [active, setActive] = useState("summary");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("not_now");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<unknown>();
  const approveRef = useRef<HTMLButtonElement>(null);
  const [decided, setDecided] = useState<string | null>(null);
  // A and X in the inbox open the review with the decision ready: approve focused, or the reasons open.
  const intent = useSearch({ strict: false }) as { intent?: "approve" | "reject" };
  const loaded = Boolean(plan.data);
  useEffect(() => {
    if (!loaded || !pending) return;
    if (intent.intent === "approve") approveRef.current?.focus();
    if (intent.intent === "reject") setRejecting(true);
  }, [intent.intent, loaded, pending]);

  const outline = useMemo(() => (plan.data ? planOutline(plan.data) : []), [plan.data]);
  const cards = useMemo(() => outline.filter((entry) => entry.card), [outline]);
  const toggleViewed = useCallback((id: string, value?: boolean) => setViewed((current) => {
    const next = new Set(current);
    if (value ?? !next.has(id)) next.add(id); else next.delete(id);
    writePref(viewedKey(planId), JSON.stringify([...next]));
    return next;
  }), [planId]);
  const back = useCallback(() => { void navigate({ to: "/inbox" }); }, [navigate]);

  const decide = useCallback(async (kind: "approve" | "reject") => {
    if (!plan.data) return;
    setBusy(kind); setError(undefined);
    try {
      if (kind === "approve") await api.post(`/api/plans/${encodeURIComponent(plan.data.id)}/approve`);
      else await api.post(`/api/plans/${encodeURIComponent(plan.data.id)}/reject`, { reason, note: note.trim() || null });
      await client.invalidateQueries();
      setRejecting(false);
      const done = kind === "approve" ? I.approved : reason === "revise" ? I.sentBack : I.rejected;
      setDecided(done);
      notify.success(done);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  }, [client, note, plan.data, reason]);

  useEffect(() => {
    // J/K move between cards, V marks the current one viewed, A approves, X rejects, Esc returns to the inbox (§0.4d).
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || typing(event.target) || document.querySelector("[role=dialog]:not([aria-modal=false])")) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const index = cards.findIndex((card) => card.id === active);
      const go = (next: number) => {
        const card = cards[Math.min(cards.length - 1, Math.max(0, next))];
        if (!card) return;
        setActive(card.id);
        document.getElementById(card.id)?.scrollIntoView({ block: "start", behavior: "smooth" });
      };
      if (key === "j" || key === "ArrowDown") go(index + 1);
      else if (key === "k" || key === "ArrowUp") go(index - 1);
      else if (key === "v" && cards.some((card) => card.id === active)) toggleViewed(active);
      else if (key === "a" && pending) approveRef.current?.focus();
      else if (key === "x" && pending) setRejecting(true);
      else if (key === "Escape") back();
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, back, cards, pending, toggleViewed]);

  const product = products.data?.find((candidate) => candidate.id === productId);
  const crumbs = [{ label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } }, { label: A.inbox, to: "/inbox" }, { label: R.title }];
  if (plan.isLoading || metrics.isLoading || goals.isLoading) return <Page crumbs={crumbs}><Skeleton shape="object" /></Page>;
  if (!plan.data) return <Page crumbs={crumbs}><ErrorText error={plan.error} /></Page>;
  const data = plan.data;
  const body = data.body;
  const metricName = (id: string | null) => (id && metrics.data?.find((m) => m.definition.id === id)?.definition.name) || "–";
  const goalStatement = (id: string | null) => (id && goals.data?.goals.find((g) => g.id === id)?.statement) || null;
  const engine = I.engine[data.engine.name] ?? data.engine.name;
  const logo = agentEngine(data.engine.name);
  const viewedCount = cards.filter((card) => viewed.has(card.id)).length;
  const totals = planTotals(data);

  return <Page crumbs={crumbs} wide>
    <div className="z-review">
      <ReviewOutline active={active} onNavigate={setActive} items={outline.map((entry) => ({ id: entry.id, label: entry.label, icon: entry.id === "checks" ? <ListChecksIcon size={16} /> : entry.id.startsWith("campaign-") ? <CubeIcon size={16} /> : entry.id.startsWith("action-") ? <GitBranchIcon size={16} /> : entry.id === "not-chosen" ? <ArchiveIcon size={16} /> : <ArticleIcon size={16} />, level: entry.card ? 2 : 1, group: entry.group, viewed: entry.card ? viewed.has(entry.id) : undefined }))} />
      <div className="z-review-main">
        <header className="z-review-head" id="summary">
          <div className="z-inline"><StatusChip kind={planStatus(data.status)} soft>{I.planStatus[data.status]}</StatusChip>{data.scope === "addendum" && <Chip>{I.addendum}</Chip>}</div>
          <h1>{data.scope === "addendum" ? R.addendumTitle : R.weekTitle}</h1>
          <p className="z-muted z-small z-review-by">{logo && <AgentLogo engine={logo} size={14} />}{R.submitted(engine, dateTime(data.created_at))}{data.expires_at && pending ? ` · ${I.expires(data.expires_at)}` : ""}</p>
        </header>
        {decided ? <Callout tone="good"><p>{decided}</p><p><Button size="sm" onClick={back}>{R.back}</Button></p></Callout> : !pending && <Callout>{I.closed}</Callout>}
        <Glance items={[
          ...(totals.campaigns ? [{ label: I.campaigns, value: number(totals.campaigns) }] : []),
          { label: I.actions, value: number(totals.actions) },
          { label: I.yourTime, value: minutes(totals.requestedMinutes), note: R.timeLeft(totals.remainingMinutes) },
          ...(totals.spendUsd !== null ? [{ label: I.cost, value: usd(totals.spendUsd) }] : []),
          { label: R.checks, value: R.checksPassed(totals.passed, totals.checks) },
        ]} />
        <AgentText name={engine} engine={data.engine.name} lines={4}>{body.summary}</AgentText>
        {body.nothing_this_week && <AgentText name={engine} engine={data.engine.name} label={I.nothingThisWeek} lines={4}>{body.nothing_this_week.reason}</AgentText>}

        <section id="checks" className="z-review-section">
          <h2>{R.checks} <span className="z-count">{R.checksPassed(totals.passed, totals.checks)}</span></h2>
          <CheckList items={planChecks(data)} foldPassed />
        </section>

        {body.campaigns.length > 0 && <section className="z-review-section">
          <h2>{I.campaigns} <span className="z-count">{number(body.campaigns.length)}</span></h2>
          {body.campaigns.map((campaign) => {
            const id = `campaign-${campaign.ref}`;
            return <ReviewCard key={id} id={id} kicker={R.newCampaign} title={campaign.title} viewed={viewed.has(id)} onViewedChange={(value) => toggleViewed(id, value)}>
              <Glance size="sm" items={[
                { label: I.metrics, value: campaign.metric_ids.length ? campaign.metric_ids.map(metricName).join(" · ") : null },
                { label: I.measurement, value: I.windowDays(campaign.window_days) },
              ]} />
              <AgentText name={engine} engine={data.engine.name} label={I.hypothesis} lines={3}>{campaign.hypothesis}</AgentText>
              <More>
                <PropertyList>
                  <PropertyRow label={I.goal}>{goalStatement(campaign.goal_id)}</PropertyRow>
                  <PropertyRow label={R.evidence}>{I.evidence(campaign.evidence.length)}</PropertyRow>
                </PropertyList>
              </More>
            </ReviewCard>;
          })}
        </section>}

        {body.actions.length > 0 && <section className="z-review-section">
          <h2>{I.actions} <span className="z-count">{number(body.actions.length)}</span></h2>
          {body.actions.map((action) => <ActionCard key={action.ref} plan={data} action={action} engine={engine} metricName={metricName} viewed={viewed.has(`action-${action.ref}`)} onViewed={(value) => toggleViewed(`action-${action.ref}`, value)} />)}
        </section>}

        {body.not_chosen.length > 0 && <section id="not-chosen" className="z-review-section">
          <More label={I.notChosen} count={body.not_chosen.length}>
            {body.not_chosen.map((entry, index) => <AgentText key={index} name={engine} engine={data.engine.name} lines={2}>{entry.why}</AgentText>)}
          </More>
        </section>}
        <ErrorText error={error} />
      </div>
    </div>
    {pending && <ReviewBar status={<StatusChip kind="proposed" />} viewed={viewedCount} total={cards.length}>
      <Button tone="ghost" icon={<ArrowLeftIcon />} onClick={back}>{R.back} <Kbd>Esc</Kbd></Button>
      <Button disabled={busy !== null} onClick={() => setRejecting(true)}>{I.reject} <Kbd>X</Kbd></Button>
      <Button ref={approveRef} tone="primary" busy={busy === "approve"} disabled={busy !== null} onClick={() => void decide("approve")}>{R.approve} <Kbd>A</Kbd></Button>
    </ReviewBar>}
    <Modal open={rejecting} onOpenChange={(open) => { if (!open) setRejecting(false); }} title={reason === "revise" ? I.confirmRevise : I.confirmReject}
      footer={<><Button tone="ghost" onClick={() => setRejecting(false)}>{I.cancel}</Button><Button tone="danger" busy={busy === "reject"} disabled={busy !== null || (reason === "revise" && !note.trim())} onClick={() => void decide("reject")}>{reason === "revise" ? I.confirmRevise : I.confirmReject}</Button></>}>
      <fieldset className="z-fieldset">
        <legend>{I.rejectReason}</legend>
        <ul className="z-check">{Object.entries(I.rejectReasons).map(([value, label]) => <li key={value}>
          <input id={`reject-${value}`} type="radio" name="reject-reason" value={value} checked={reason === value} disabled={busy !== null} onChange={() => setReason(value)} />
          <label htmlFor={`reject-${value}`}>{label}</label>
        </li>)}</ul>
      </fieldset>
      <div className="z-field">
        <label htmlFor="reject-note">{reason === "revise" ? I.rejectNoteRequired : I.rejectNote}</label>
        <textarea id="reject-note" className="z-textarea" rows={3} maxLength={600} value={note} disabled={busy !== null} required={reason === "revise"} onChange={(event) => setNote(event.target.value)} />
        {reason === "revise" && <p className="z-small z-muted">{I.reviseNote}</p>}
      </div>
      <ErrorText error={error} />
    </Modal>
  </Page>;
}

/** `?intent=approve|reject`: the inbox's A and X open the review with the decision ready. */
export const planReviewSearch = (search: Record<string, unknown>): { intent?: "approve" | "reject" } =>
  search.intent === "approve" || search.intent === "reject" ? { intent: search.intent } : {};

/** `/inbox/plans/:id` from before the review had its own page: the plan's product decides where it opens. */
export function PlanRedirect() {
  const { planId } = useParams({ strict: false }) as { planId: string };
  const navigate = useNavigate();
  const plan = useQuery(q.agentPlan(planId));
  const done = useRef(false);
  const productId = plan.data?.product_id;
  useEffect(() => {
    if (!productId || done.current) return;
    done.current = true;
    void navigate({ to: "/p/$productId/plans/$planId", params: { productId, planId }, replace: true });
  }, [navigate, planId, productId]);
  if (plan.error) return <Page crumbs={[{ label: A.inbox, to: "/inbox" }]}><ErrorText error={plan.error} /></Page>;
  return <Page crumbs={[]}><Skeleton shape="object" /></Page>;
}

function ActionCard({ plan, action, engine, metricName, viewed, onViewed }: {
  plan: C.AgentPlan; action: C.AgentPlan["body"]["actions"][number]; engine: string; metricName: (id: string | null) => string; viewed: boolean; onViewed: (value: boolean) => void;
}) {
  const check = plan.checked.authorship.find((entry) => entry.ref === action.ref);
  const campaign = action.campaign_ref ? plan.body.campaigns.find((b) => b.ref === action.campaign_ref)?.title : null;
  const m = action.measurement;
  const messages = (plan.outreach_messages ?? []).filter((message) => message.action_ref === action.ref);
  const expectation = m.expectation ? [I.direction[m.expectation.direction] ?? m.expectation.direction, m.expectation.range
    ? m.expectation.range.low !== null && m.expectation.range.high !== null ? I.between(m.expectation.range.low, m.expectation.range.high)
      : m.expectation.range.low !== null ? I.atLeast(m.expectation.range.low) : m.expectation.range.high !== null ? I.atMost(m.expectation.range.high) : null
    : null].filter(Boolean).join(" · ") : null;
  return <ReviewCard id={`action-${action.ref}`} kicker={<>{I.actionKind[action.kind] ?? action.kind} · <ChannelName channel={action.channel} /> · <span className="z-mono" title={I.where}>{action.attribution_unit}</span></>} title={action.title} viewed={viewed} onViewedChange={onViewed}>
    <Glance size="sm" items={[
      { label: I.yourTime, value: minutes(action.person_minutes) },
      { label: I.tier, value: I.authorship[action.authorship] ?? action.authorship },
      { label: I.approval, value: <Chip variant="soft" tone={check?.grant_id || check?.default_grant ? "teal" : "amber"} dot>{check?.grant_id ? I.covered : check?.default_grant ? I.coveredDefault : I.notCovered}</Chip> },
      { label: I.measurement, value: I.mode[m.mode] ?? m.mode, note: m.mode === "observational" ? `${metricName(m.primary_metric_id)} · ${m.window_days ? I.windowDays(m.window_days) : "–"}` : undefined },
      ...(action.spend ? [{ label: I.cost, value: usd(action.spend.amount_usd) }] : []),
    ]} />
    <AgentText name={engine} engine={plan.engine.name} label={R.why} lines={2}>{action.why}</AgentText>
    {(action.outreach || expectation || m.acceptance.length > 0) && <PropertyList>
      {action.outreach && <PropertyRow label={I.actionKind.outreach}>{I.outreach(action.outreach.recipients, I.audience[action.outreach.audience] ?? action.outreach.audience)}</PropertyRow>}
      {expectation && <PropertyRow label={I.expectation}>{expectation}</PropertyRow>}
      {m.acceptance.length > 0 && <PropertyRow label={I.acceptance}><PointList items={m.acceptance} /></PropertyRow>}
    </PropertyList>}
    {action.post?.draft && <AgentText name={engine} engine={plan.engine.name} label={I.draft} lines={4}>{action.post.draft}</AgentText>}
    {action.post && action.post.points.length > 0 && <AgentText name={engine} engine={plan.engine.name} label={I.points} lines={4}><ul className="z-points">{action.post.points.map((point, index) => <li key={index}>{point}</li>)}</ul></AgentText>}
    {action.replies && <div className="z-flow"><h4>{I.replies} <span className="z-count">{number(action.replies.items.length)}</span></h4>{action.replies.items.map((reply, index) => <div key={index} className="z-review-item">
      <a className="z-small" href={reply.target_url} target="_blank" rel="noreferrer noopener">{reply.target_author ? `@${reply.target_author}` : I.replyTarget}</a>
      {reply.target_excerpt && <blockquote className="z-reply-quote z-agent-text">{reply.target_excerpt}</blockquote>}
      <AgentText name={engine} engine={plan.engine.name} label={I.draft} lines={3}>{reply.body}</AgentText>
    </div>)}</div>}
    {messages.length > 0 && <div className="z-flow"><h4>{I.messages} <span className="z-count">{number(messages.length)}</span></h4>{messages.map((message) => <AgentText key={message.position} name={engine} engine={plan.engine.name} label={`${message.name} · ${message.address}`} lines={3}>
      {message.subject && <>{I.subject}: {message.subject}{"\n\n"}</>}{message.body}
    </AgentText>)}</div>}
    <More label={R.basis}>
      <PropertyList>
        <PropertyRow label={I.campaign}>{campaign ?? I.existingCampaign}</PropertyRow>
        {action.playbook_id && <PropertyRow label={I.playbook}><span className="z-mono">{action.playbook_id}</span></PropertyRow>}
        <PropertyRow label={R.evidence}>{I.evidence(action.evidence.length)}</PropertyRow>
      </PropertyList>
    </More>
  </ReviewCard>;
}
